/**
 * Saved replies in the composer: which ones match what the agent typed, and
 * the text they insert. Pure, so it is unit-tested on its own.
 */

import { AntChatSavedReply } from '../../hooks/chat/api';

/** The variables a saved reply may use, filled in when it is inserted. */
export const REPLY_VARIABLES = ['{{rider_first_name}}', '{{agent_first_name}}'] as const;

/** Said instead of a name the console does not know. */
export const UNKNOWN_RIDER = 'there';
export const UNKNOWN_AGENT = 'Support';

/** The first word of a name: "Ada" of "Ada Lovelace"; '' when there is none. */
export const firstNameOf = (name: string | null | undefined): string =>
  (name ?? '').trim().split(/\s+/)[0] ?? '';

/**
 * The reply's body with {{rider_first_name}} and {{agent_first_name}} filled
 * in ("there" and "Support" when the name is unknown). Any other {{...}} is
 * left as written, so a typo shows before it is sent.
 */
export function fillReplyVariables(
  body: string,
  names: { riderFirstName?: string | null; agentFirstName?: string | null }
): string {
  const values: Record<string, string> = {
    rider_first_name: names.riderFirstName?.trim() || UNKNOWN_RIDER,
    agent_first_name: names.agentFirstName?.trim() || UNKNOWN_AGENT,
  };
  return body.replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (match, key: string) =>
    Object.prototype.hasOwnProperty.call(values, key.toLowerCase())
      ? values[key.toLowerCase()]
      : match
  );
}

/**
 * What the agent is searching for when the composer starts with "/" (the
 * shortcut): the text after it, or null when the composer is not a shortcut
 * (no leading slash, or a second line).
 */
export function slashQuery(input: string): string | null {
  if (!input.startsWith('/') || input.includes('\n')) return null;
  return input.slice(1);
}

/** Lowercase, without accents: "Café" and "cafe" match. */
const fold = (text: string): string =>
  text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Every character of `needle`, in order, somewhere in `haystack`. */
const isSubsequence = (needle: string, haystack: string): boolean => {
  let i = 0;
  for (let j = 0; j < haystack.length && i < needle.length; j += 1) {
    if (haystack[j] === needle[i]) i += 1;
  }
  return i === needle.length;
};

/**
 * How well one word of the query matches a reply; 0 is no match. Title
 * start > a title word's start > anywhere in the title > the letters in
 * order in the title ("unlk" finds "Unlock help") > anywhere in the body.
 */
function wordScore(word: string, title: string, body: string): number {
  if (title.startsWith(word)) return 5;
  if (title.split(/[^a-z0-9]+/).some((w) => w.startsWith(word))) return 4;
  if (title.includes(word)) return 3;
  if (word.length > 1 && isSubsequence(word, title)) return 2;
  if (body.includes(word)) return 1;
  return 0;
}

/**
 * The replies that match `query`, best first (then by title). Every word of
 * the query must match the title or the body. An empty query returns every
 * reply, by title.
 */
export function searchSavedReplies<T extends Pick<AntChatSavedReply, 'title' | 'body'>>(
  replies: T[],
  query: string
): T[] {
  const words = fold(query).split(/\s+/).filter(Boolean);
  const byTitle = (a: T, b: T) => a.title.localeCompare(b.title);
  if (words.length === 0) return [...replies].sort(byTitle);

  const scored: Array<{ reply: T; score: number }> = [];
  replies.forEach((reply) => {
    const title = fold(reply.title);
    const body = fold(reply.body);
    let score = 0;
    for (const word of words) {
      const s = wordScore(word, title, body);
      if (s === 0) return;
      score += s;
    }
    scored.push({ reply, score });
  });
  return scored
    .sort((a, b) => b.score - a.score || byTitle(a.reply, b.reply))
    .map((s) => s.reply);
}

/** Where a reply shows: every system, or one. */
export const replyScopeLabel = (
  reply: Pick<AntChatSavedReply, 'systemId' | 'system'>
): string => (reply.systemId ? reply.system?.name || 'One system' : 'All systems');
